import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Connect", "Secure", "Collaborate", "Compute", "Source", "Consulting", "Other"];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">{label}</span>
    <input
      {...props}
      className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-smipl-orange transition-colors"
    />
  </label>
);

const ContactModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "Connect", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — our team will reach out shortly.");
      setForm({ name: "", email: "", company: "", interest: "Connect", message: "" });
      onClose();
    } catch {
      toast.error("Could not send right now. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-navy-deep/80 backdrop-blur-md"
          onClick={onClose}
          data-testid="contact-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="contact-modal"
            className="w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-3xl border border-white/12 bg-navy-ink shadow-2xl shadow-black/60 p-8 md:p-10"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-smipl-orange">Start a Conversation</p>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold">Tell us what you're building.</h3>
              </div>
              <button data-testid="contact-modal-close" onClick={onClose} className="p-2 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-smipl-orange transition-colors">
                <X size={17} />
              </button>
            </div>
            <form onSubmit={submit} className="mt-7 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" />
                <Field label="Email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Organization" />
                <label className="block">
                  <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Interested in</span>
                  <select
                    data-testid="contact-interest-select"
                    value={form.interest}
                    onChange={set("interest")}
                    className="w-full rounded-xl border border-white/10 bg-navy-ink px-4 py-3 text-sm text-white outline-none focus:border-smipl-orange transition-colors"
                  >
                    {INTERESTS.map((i) => <option key={i}>{i}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="block font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-2">Requirement</span>
                <textarea
                  data-testid="contact-message-input"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Briefly describe your requirement…"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-smipl-orange transition-colors resize-none"
                />
              </label>
              <button
                data-testid="contact-submit-btn"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 rounded-full bg-smipl-orange text-white py-3.5 text-sm font-semibold hover:bg-smipl-amber disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
